import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Package, Archive, ChevronDown } from 'lucide-react';
import { CodeArtifact, CodeProject, CodeSession, CodeTarget, CYAN, FinalActionChoice, STATUS_LABEL, SessionStatus } from './code/types';
import { INITIAL_ARTIFACTS, INITIAL_SESSIONS, PROJECTS } from './code/mockData';
import { NewSessionInput } from './code/NewSessionInput';
import { SessionSheet } from './code/SessionSheet';
import { AllArtifactsSheet } from './code/AllArtifactsSheet';
function titleFromRequest(text: string): string {
  return text.trim().split(/\s+/).slice(0, 7).join(' ');
}
export function CodeView() {
  const [projects] = useState<CodeProject[]>(PROJECTS);
  const [sessions, setSessions] = useState<CodeSession[]>(INITIAL_SESSIONS);
  const [artifacts, setArtifacts] = useState<CodeArtifact[]>(INITIAL_ARTIFACTS);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [allArtifactsOpen, setAllArtifactsOpen] = useState(false);
  const [newSessionTarget, setNewSessionTarget] = useState<string | null>(null);
  const activeSession = useMemo(() => sessions.find(s => s.id === activeSessionId) ?? null, [sessions, activeSessionId]);
  const sessionsByTarget = useMemo(() => {
    const map = new Map<string, CodeSession[]>();
    for (const s of sessions) {
      if (!map.has(s.targetId)) map.set(s.targetId, []);
      map.get(s.targetId)!.push(s);
    }
    return map;
  }, [sessions]);
  const groups = useMemo(() => {
    const out: {
      project: CodeProject;
      target: CodeTarget;
    }[] = [];
    for (const project of projects) {
      for (const target of project.targets) {
        out.push({
          project,
          target
        });
      }
    }
    return out;
  }, [projects]);
  const breadcrumbFor = (session: CodeSession) => {
    const target = projects.flatMap(p => p.targets).find(t => t.id === session.targetId);
    const project = projects.find(p => p.id === target?.projectId);
    return `${project?.name ?? ''} · ${target?.name ?? ''}`;
  };
  const activeArtifacts = useMemo(() => {
    if (!activeSession) return [];
    return artifacts.filter(a => a.sessionId === activeSession.id);
  }, [activeSession, artifacts]);
  const updateSession = (id: string, patch: Partial<CodeSession> | ((s: CodeSession) => Partial<CodeSession>)) => {
    setSessions(prev => prev.map(s => {
      if (s.id !== id) return s;
      const p = typeof patch === 'function' ? patch(s) : patch;
      return {
        ...s,
        ...p
      };
    }));
  };
  const handleCreateSession = (target: CodeTarget, text: string) => {
    const id = `new-${Date.now()}`;
    const newSession: CodeSession = {
      id,
      targetId: target.id,
      title: titleFromRequest(text),
      originalRequest: text,
      createdLabel: 'just now',
      startedAt: 'Started just now',
      status: 'running',
      artifactIds: [],
      messages: [{
        id: `${id}-u1`,
        role: 'user',
        content: text,
        timestamp: 'now'
      }, {
        id: `${id}-a1`,
        role: 'agent',
        content: "On it — let me take a look.",
        timestamp: 'now'
      }]
    };
    setSessions(prev => [newSession, ...prev]);
    setNewSessionTarget(null);
    setActiveSessionId(id);
  };
  const handleInterrupt = () => {
    if (!activeSession) return;
    updateSession(activeSession.id, s => ({
      status: 'interrupted',
      messages: [...s.messages, {
        id: `sys-${Date.now()}`,
        role: 'system',
        content: 'Interrupted by Matt',
        timestamp: 'now'
      }]
    }));
  };
  const handleSendMessage = (content: string) => {
    if (!activeSession) return;
    const now = Date.now();
    updateSession(activeSession.id, s => ({
      status: 'running' as SessionStatus,
      finalAction: undefined,
      messages: [...s.messages, {
        id: `u-${now}`,
        role: 'user',
        content,
        timestamp: 'now'
      }, {
        id: `a-${now}`,
        role: 'agent',
        content: "Got it — continuing.",
        timestamp: 'now'
      }]
    }));
  };
  const handlePickChoice = (choice: FinalActionChoice) => {
    if (!activeSession) return;
    const now = Date.now();
    updateSession(activeSession.id, s => ({
      status: 'running' as SessionStatus,
      finalAction: undefined,
      messages: [...s.messages, {
        id: `u-${now}`,
        role: 'user',
        content: `${choice.key}. ${choice.label}`,
        timestamp: 'now'
      }, {
        id: `a-${now}`,
        role: 'agent',
        content: 'Thanks — proceeding with that approach now.',
        timestamp: 'now'
      }]
    }));
    setArtifacts(prev => prev.map(a => {
      if (a.sessionId !== activeSession.id) return a;
      if (a.kind !== 'hitl-step' || a.status !== 'pending') return a;
      return {
        ...a,
        status: 'resolved',
        answer: `${choice.key}. ${choice.label}`
      };
    }));
  };
  const handleAnswerHitl = (artifactId: string, answer: string) => {
    setArtifacts(prev => prev.map(a => {
      if (a.id !== artifactId) return a;
      if (a.kind !== 'hitl-step') return a;
      return {
        ...a,
        status: 'resolved',
        answer
      };
    }));
    const artifact = artifacts.find(a => a.id === artifactId);
    if (!artifact) return;
    const targetSession = sessions.find(s => s.id === artifact.sessionId);
    if (!targetSession) return;
    updateSession(targetSession.id, s => ({
      status: 'running' as SessionStatus,
      finalAction: undefined,
      messages: [...s.messages, {
        id: `u-${Date.now()}`,
        role: 'user',
        content: answer,
        timestamp: 'now'
      }, {
        id: `a-${Date.now() + 1}`,
        role: 'agent',
        content: 'Thanks — picking that up.',
        timestamp: 'now'
      }]
    }));
  };
  return <div className="min-h-[calc(100vh-56px)] pt-16 pb-32 px-6" data-id="element-627">
      <div className="max-w-[900px] mx-auto w-full" data-id="element-628">
        <div className="flex items-center justify-between mb-6" data-id="element-629">
          <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight" data-id="element-630">Code</h1>
          <div className="flex items-center gap-1.5" data-id="element-631">
            <GhostButton icon={<Package size={12} strokeWidth={1.8} data-id="element-633" />} label="All artifacts" onClick={() => setAllArtifactsOpen(true)} data-id="element-632" />
            <NewDropdown data-id="element-634" />
          </div>
        </div>

        <div className="space-y-8" data-id="element-635">
          {groups.map(({
          project,
          target
        }) => {
          const targetSessions = sessionsByTarget.get(target.id) ?? [];
          const isCreating = newSessionTarget === target.id;
          return <section key={target.id} data-id="element-636">
                <div className="flex items-baseline gap-2 mb-3" data-id="element-637">
                  <h2 className="text-[14px] text-[#8a8680]" data-id="element-638">
                    {project.name}: {target.name}
                  </h2>
                  <span className="text-[11px] text-[#8a8680]/50" data-id="element-639">
                    {targetSessions.length} session{targetSessions.length === 1 ? '' : 's'}
                  </span>
                </div>

                <div className="space-y-0.5" data-id="element-640">
                  {targetSessions.map(session => <SessionRow key={session.id} session={session} onClick={() => setActiveSessionId(session.id)} data-id="element-641" />)}
                  {targetSessions.length === 0 && !isCreating && <div className="text-[12px] text-[#8a8680]/50 italic py-1.5 px-2" data-id="element-642">
                      No sessions yet.
                    </div>}
                </div>

                <NewSessionInput isOpen={isCreating} onSubmit={text => handleCreateSession(target, text)} onCancel={() => setNewSessionTarget(null)} data-id="element-643" />

                {!isCreating && <button onClick={() => setNewSessionTarget(target.id)} className="mt-2 flex items-center gap-1.5 text-[12px] text-[#8a8680] hover:text-[#4ac0d4] transition-colors px-2 py-1" data-id="element-644">
                    <Plus size={11} strokeWidth={2} data-id="element-645" />
                    New
                  </button>}
              </section>;
        })}
        </div>

        <div className="mt-12 pt-6 border-t border-white/[0.04] flex justify-center" data-id="element-646">
          <button disabled title="Showing projects with activity in the last month" className="flex items-center gap-2 px-3 py-1.5 text-[12px] text-[#8a8680]/40 cursor-not-allowed" data-id="element-647">
            <Archive size={12} strokeWidth={1.8} data-id="element-648" />
            <span data-id="element-649">More projects</span>
          </button>
        </div>
      </div>

      <SessionSheet session={activeSession} artifacts={activeArtifacts} breadcrumb={activeSession ? breadcrumbFor(activeSession) : ''} onClose={() => setActiveSessionId(null)} onInterrupt={handleInterrupt} onSendMessage={handleSendMessage} onPickChoice={handlePickChoice} onAnswerHitl={handleAnswerHitl} data-id="element-650" />

      <AllArtifactsSheet isOpen={allArtifactsOpen} onClose={() => setAllArtifactsOpen(false)} projects={projects} sessions={sessions} artifacts={artifacts} onAnswerHitl={handleAnswerHitl} onViewSession={id => {
      setAllArtifactsOpen(false);
      setActiveSessionId(id);
    }} data-id="element-651" />
    </div>;
}
function SessionRow({
  session,
  onClick
}: {
  session: CodeSession;
  onClick: () => void;
}) {
  return <button onClick={onClick} className="w-full flex items-center gap-3 py-2 px-2 -mx-2 rounded-[8px] hover:bg-white/[0.03] transition-colors text-left" data-id="element-652">
      <span className="text-[11px] text-[#8a8680]/70 w-[88px] flex-shrink-0" data-id="element-653">
        {session.createdLabel}
      </span>
      <span className={`text-[13px] flex-1 truncate ${session.status === 'done' ? 'text-[#8a8680]' : 'text-[#e8e4df]'}`} data-id="element-654">
        {session.title}
      </span>
      <StatusBadge status={session.status} data-id="element-655" />
    </button>;
}
function StatusBadge({
  status
}: {
  status: SessionStatus;
}) {
  const isRunning = status === 'running';
  const dimmed = status === 'done' || status === 'interrupted';
  return <div className="inline-flex items-center justify-center w-[88px] h-[22px] rounded-full text-[10px] uppercase tracking-wide flex-shrink-0" style={{
    background: `${CYAN}${isRunning ? '22' : '15'}`,
    color: CYAN,
    border: `1px solid ${CYAN}33`,
    opacity: dimmed ? 0.55 : 1
  }} data-id="element-656">
      {isRunning && <motion.div className="w-1.5 h-1.5 rounded-full mr-1.5" style={{
      background: CYAN
    }} animate={{
      opacity: [0.4, 1, 0.4]
    }} transition={{
      duration: 1.6,
      repeat: Infinity
    }} data-id="element-657" />}
      <span data-id="element-658">{STATUS_LABEL[status]}</span>
    </div>;
}
function NewDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return <div ref={ref} className="relative" data-id="element-659">
      <button onClick={() => setOpen(prev => !prev)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] text-[#8a8680] hover:text-[#e8e4df] hover:bg-white/[0.04] transition-colors" data-id="element-660">
        <Plus size={12} strokeWidth={1.8} data-id="element-661" />
        <span data-id="element-662">New</span>
        <ChevronDown size={11} strokeWidth={1.8} className={`transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-663" />
      </button>
      <AnimatePresence data-id="element-664">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.15
      }} className="absolute right-0 top-full mt-1 w-[140px] bg-[#1c1c22] border border-white/[0.06] rounded-[12px] shadow-xl overflow-hidden z-50" data-id="element-665">
            <button disabled title="Coming soon" className="w-full text-left px-3 py-2 text-[12px] text-[#8a8680]/50 cursor-not-allowed" data-id="element-666">
              Project
            </button>
            <button disabled title="Coming soon" className="w-full text-left px-3 py-2 text-[12px] text-[#8a8680]/50 cursor-not-allowed border-t border-white/[0.04]" data-id="element-667">
              Target
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function GhostButton({
  icon,
  label,
  onClick,
  disabled,
  title
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return <button onClick={onClick} disabled={disabled} title={title} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] transition-colors ${disabled ? 'text-[#8a8680]/40 cursor-not-allowed' : 'text-[#8a8680] hover:text-[#e8e4df] hover:bg-white/[0.04]'}`} data-id="element-668">
      {icon}
      <span data-id="element-669">{label}</span>
    </button>;
}